import React from 'react';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import { navColors } from './constants';

export const regionTabs = ['North', 'South', 'West', 'East', 'Central'];
export const getColor = (pathname: string) =>
  navColors.filter((nav: any) => {
    return nav.pathName === pathname || pathname.includes(nav.pathName);
  });

export const getBlogName = (blogType: string, list: any) => {
  return list.filter((type: any) => type.value === blogType)[0]?.label;
};

export const getCountryFromSlug = (countrySlug: string, list: any) => {
  return list.filter((type: any) => type.countrySlug === countrySlug)[0]?.label
}

export const getCountryValueFromSlug = (countrySlug: string, list: any) => {
  return list.filter((type: any) => type.countrySlug === countrySlug)[0]?.value
}


export const getSlugFromCountryUni = (country: string, list: any) => {
  return list.filter((type: any) => type.label === country)[0]?.countrySlug
}

export const getSlugFromCountryUniDetail = (country: string, list: any) => {
  return list.filter((type: any) => type.label.replace(/\s+/g, '') === country)[0]?.countrySlug
}

export const getSlugFromCountry = (country: string, list: any) => {
  return list.filter((type: any) => type.label === country)[0]?.slug
}

export const getUpdatedValue = (value: any) =>
  value?.toString().replace(/-/g, '_');

export const dynamicJotformScript = (jotformId: string, openOnLoad: boolean) => {
  return `new JotformFeedback({
    formId: '${jotformId}',
    base: 'https://form.jotform.com/',
    windowTitle: 'Enquiry Form',
    background: '#FFA500',
    fontColor: '#FFFFFF',
    type: '0',
    height: 550,
    width: 570,
    openOnLoad: ${openOnLoad}
  })`
}

export const isFrappeOrJotForm = (str?: string): string => {
  if(!str) {
    return '.kc-enquire-form';
  }
  const isJotForm =  /^\d+$/.test(str);
  if(isJotForm) {
    return '.lightbox-'+str;
  }
  return '.kc-enquire-form-'+str;
}

export const isFrappeOrJotFormClass = (str?: string | number): string => {
  if(!str) {
    return 'kc-enquire-form';
  }
  const isJotForm =  /^\d+$/.test(str+"");
  if(isJotForm) {
    return 'lightbox-'+str;
  }
  return 'kc-enquire-form-'+str;
}


export function extractIntroParagraphs(html: string, count = 2) {
  let intro = "";
  let description = html;

  // Normalize whitespace at the beginning
  let trimmed = html.trimStart();

  // If the very first tag isn’t <p>, return as-is
  if (!trimmed.toLowerCase().startsWith("<p")) {
    return { intro: "", description: html };
  }

  // Regex anchored at the start — matches first <p>...</p>
  const firstParagraphRegex = /^<p[\s\S]*?<\/p>/i;

  for (let i = 0; i < count; i++) {
    const match = trimmed.match(firstParagraphRegex);
    if (!match) break;

    intro += match[0];
    trimmed = trimmed.replace(firstParagraphRegex, "").trimStart();
  }

  description = trimmed;

  return { intro, description };
}

const parseStyle = (styleString: string) =>
  Object.fromEntries(
    styleString
      .split(';')
      .filter(Boolean)
      .map(s => {
        const [key, value] = s.split(':');
        return [
          key.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase()),
          value.trim()
        ];
      })
  );

/**
 * Parse hero HTML safely for SSR.
 * Extracts first `count` <p> elements and returns React nodes.
 */
export function parseHeroHTML(html: string, count = 2) {
  let paragraphCount = 0;

  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (domNode.type === 'tag' && domNode.name === 'p') {
        paragraphCount++;
        if (paragraphCount > count) return null; // skip paragraphs after first N

        const { attribs, children } = domNode;
        return React.createElement(
          'p',
          {
            className: attribs?.class,
            style: attribs?.style ? parseStyle(attribs.style) : undefined,
          },
          domToReact(children, options)
        );
      }

      // Anchors must keep href/target/rel so links (and TOC hash links) work
      if (domNode.type === 'tag' && domNode.name === 'a') {
        const { attribs, children } = domNode;
        return React.createElement(
          'a',
          {
            href: attribs?.href,
            target: attribs?.target,
            rel: attribs?.rel,
            title: attribs?.title,
            id: attribs?.id,
            className: attribs?.class,
            style: attribs?.style ? parseStyle(attribs.style) : undefined,
          },
          domToReact(children, options)
        );
      }

      // Optionally allow nested inline tags
      if (
        domNode.type === 'tag' &&
        ['strong', 'em', 'span'].includes(domNode.name)
      ) {
        const { attribs, children } = domNode;
        return React.createElement(
          domNode.name,
          {
            className: attribs?.class,
            style: attribs?.style ? parseStyle(attribs.style) : undefined,
          },
          domToReact(children, options)
        );
      }
    },
  };

  return parse(html, options);
}

export const getMobileImageUrl = (imageUrl?: string) => {
  if(!imageUrl) {
    return undefined;
  }
  const mobileImageUrl = imageUrl?.replace('assets.studies-overseas.com', 'assets.studies-overseas.com/mobile');
  return mobileImageUrl || imageUrl;
}

// web-540 - convert YouTube URLs to embed format
export const convertYouTubeToEmbed = (url?: string | null): string | null => {
  if (!url) return null;

  // If already an embed URL, return as is
  if (url.includes('youtube.com/embed/')) {
    return url;
  }

  let videoId = '';
  let queryParams = '';

  try {
    // Handle youtu.be format: https://youtu.be/VIDEO_ID?si=...
    if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^?&]+)(\?.*)?/);
      if (match) {
        videoId = match[1];
        // Extract query parameters from youtu.be URL
        const urlParts = url.split('?');
        if (urlParts.length > 1) {
          queryParams = '?' + urlParts[1];
        }
      }
    }
    // Handle youtube.com/watch format: https://www.youtube.com/watch?v=VIDEO_ID&si=...
    else if (url.includes('youtube.com/watch')) {
      // Ensure URL has protocol for URL constructor
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      const urlObj = new URL(fullUrl);
      videoId = urlObj.searchParams.get('v') || '';
      
      // Extract other query parameters (like si=) except 'v'
      urlObj.searchParams.delete('v');
      if (urlObj.searchParams.toString()) {
        queryParams = '?' + urlObj.searchParams.toString();
      }
    }
    // Shorts share links: https://www.youtube.com/shorts/VIDEO_ID — must use /embed/ for iframe
    else if (url.includes('youtube.com/shorts/')) {
      const match = url.match(/youtube\.com\/shorts\/([^?&/]+)/);
      if (match) {
        videoId = match[1];
      }
      const queryIndex = url.indexOf('?');
      if (queryIndex !== -1) {
        queryParams = url.slice(queryIndex);
      }
    }
    // Handle youtube.com format: https://www.youtube.com/VIDEO_ID
    else if (url.includes('youtube.com/')) {
      const match = url.match(/youtube\.com\/([^?&/]+)/);
      if (
        match &&
        !match[1].includes('watch') &&
        !match[1].includes('embed') &&
        !match[1].includes('shorts') &&
        !match[1].includes('channel') &&
        !match[1].includes('user')
      ) {
        videoId = match[1];
      }
    }

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}${queryParams}`;
    }
  } catch (error) {
    console.error('Error converting YouTube URL:', error);
  }

  // If we can't parse it, return original URL (might already be embed or invalid)
  return url;
};

/** YouTube video id for thumbnail URLs (`img.youtube.com/vi/{id}/...`), derived via the same rules as {@link convertYouTubeToEmbed}. */
export const getYouTubeVideoId = (url?: string | null): string | null => {
  if (!url) return null;
  const embed = convertYouTubeToEmbed(url);
  if (!embed) return null;
  const m = embed.match(/youtube\.com\/embed\/([^?&/]+)/);
  return m ? m[1] : null;
};